import { useEffect, useRef } from "react";

const STEP_PALETTE_SIZE = 6;

/** The step's colour token: the last step (Live) is green, the rest cycle through the palette. */
export function stepColor(index, total) {
  if (index === total - 1) return "var(--pv-live)";

  return `var(--pv-step-${(index % STEP_PALETTE_SIZE) + 1})`;
}

/**
 * One segment per step, in the colour of the step the epic is at: steps before
 * it are filled light, the current one solid. Clicking a segment moves the epic
 * there.
 */
export function StepBar({ steps, step, onChange, saving }) {
  const index = steps.indexOf(step);
  const unknown = step && index === -1;

  return (
    <div className="pv-stepbar" style={index === -1 ? undefined : { "--step-color": stepColor(index, steps.length) }}>
      <div className="pv-segments" style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}>
        {steps.map((name, i) => {
          const state = i < index ? "done" : i === index ? "now" : "";
          return (
            <button
              key={name}
              type="button"
              className={`pv-seg ${state}`}
              title={i === index ? `${name} (current)` : `Move to ${name}`}
              aria-label={`Move to ${name}`}
              disabled={saving || i === index}
              onClick={() => onChange(name)}
            />
          );
        })}
      </div>
      {saving ? <div className="pv-saving">Saving to Jira…</div> : null}
      {unknown ? (
        <div className="pv-step-warning">
          Jira says "{step}", which is not one of this view's steps.
        </div>
      ) : null}
    </div>
  );
}

/** Step names above the bars, in the table header. */
export function StepLabels({ steps }) {
  return (
    <div className="pv-step-labels" style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}>
      {steps.map((name, i) => (
        <span key={name} style={{ "--step-color": stepColor(i, steps.length) }}>
          {name}
        </span>
      ))}
    </div>
  );
}

export function Modal({ title, onClose, children, footer }) {
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Focus the first field once, on open. Re-running this on every render
  // would pull focus out of whichever field the user is typing in.
  useEffect(() => {
    panelRef.current?.querySelector("input, textarea, select")?.focus();
    const onKey = (event) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);

    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="pv-modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="pv-modal" role="dialog" aria-modal="true" aria-label={title} ref={panelRef}>
        <div className="pv-modal-head">
          <h2>{title}</h2>
          <button type="button" className="pv-icon-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="pv-modal-body">{children}</div>
        {footer ? <div className="pv-modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

export function LabelChips({ labels }) {
  return (
    <span className="pv-chips">
      {labels.map((label) => (
        <span key={label} className="pv-chip">
          {label}
        </span>
      ))}
    </span>
  );
}

export function formatRelative(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} d ago`;

  return date.toLocaleDateString();
}
